import { useCallback } from 'react'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useStudioAccessTierPending from 'Pods/ThreeDStudio/Hooks/useStudioAccessTierPending'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import { openSignInModal, openUsageModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import { MODAL_STUDIO_ACCESS_TIERS } from 'Pods/ThreeDStudio/Store/constants'

// Guards the generate controls that spend the studio quota, mirroring what the 3D
// generate button does: a logged-out visitor is prompted to sign up, and a logged-in
// user whose tier cannot generate yet gets the usage modal (start the free trial,
// subscribe, join the waitlist). Returns whether the caller may start the run.
//
// Unlike the 3D button there is no pending-intent replay: the AI Assistant composer
// holds File attachments and a live conversation that cannot survive the full reload
// the sign-up and payment flows perform.
function useRequireGenerationAccess() {
  const { isCurrentUser: isLoggedIn } = usePageConfig() || {}
  const studioAccessTier = useThreeDStudioStore(state => state.subscription.studioAccessTier)
  const isAccessPending = useStudioAccessTierPending()

  const requireGenerationAccess = useCallback(() => {
    if (!isLoggedIn) {
      openSignInModal('signup')
      return false
    }

    // The tier hydrates from /load-services.js after mount; until it lands there is
    // nothing to prompt and no way to tell whether the run is allowed.
    if (!studioAccessTier) {
      return false
    }

    if (MODAL_STUDIO_ACCESS_TIERS.has(studioAccessTier)) {
      openUsageModal()
      return false
    }

    return true
  }, [isLoggedIn, studioAccessTier])

  // Callers disable their generate control while the tier is unresolved, so the guard
  // above never silently swallows a click.
  return { requireGenerationAccess, isAccessPending }
}

export default useRequireGenerationAccess
